// Catalogo.jsx
// Ruta sugerida: src/components/Catalogo.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import BarcodeScanner from "./BarcodeScanner";
import {
    ShoppingCart, Search, Filter, X, LayoutGrid, List as ListIcon,
    Trash2, CheckCircle2, SlidersHorizontal, Mic, MicOff,
    Sparkles, Clock, BarChart3, Users, RefreshCw
} from "lucide-react";
import {
    collection,
    query,
    orderBy,
    limit,
    startAfter,
    getDocs,
    onSnapshot,
    addDoc,
    serverTimestamp,
    where,
    doc,
    setDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, CartesianGrid, Legend
} from "recharts";

/* ================================
   Config & utils
================================ */
const ARS = new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" });
const MIN_PURCHASE = 500000;
const FETCH_PAGE = 1000;
const INITIAL_VISIBLE = 40;
const LOAD_MORE_STEP = 40;
const DEFAULT_COLLECTION = "productsCatalog";
const WA_DESTINATION = "12134250460";

const BANNERS = [
    { label: "Disney", img: "/disney.png", group: "marca", value: "DISNEY" },
    { label: "Hasbro", img: "/hasbro.png", group: "marca", value: "HASBRO" },
    { label: "Marvel", img: "/marvel.png", group: "marca", value: "MARVEL" },
    { label: "Nickelodeon", img: "/nickelodeon.png", group: "marca", value: "NICKELODEON" },
    { label: "Universal", img: "/universal.png", group: "marca", value: "UNIVERSAL" },
    { label: "Star Wars", img: "/starwars.png", group: "marca", value: "STAR WARS" },
    { label: "SEGA", img: "/sega.png", group: "marca", value: "SEGA" },
    { label: "PHI PHI TOYS", img: "/phiphitoys.png", group: "marca", value: "PHI PHI TOYS" },
    { label: "RENATA", img: "/renata.png", group: "marca", value: "RENATA" },
    { label: "OSLO", img: "/oslo.png", group: "marca", value: "OSLO" },
];

const cx = (...cls) => cls.filter(Boolean).join(" ");
const safeStr = (x) => (x ?? "").toString().toLowerCase();
const isNA = (v) => { const s = safeStr(v); return s === "na" || s === "n/a"; };
function useDebounced(value, delay = 300) { const [deb, setDeb] = useState(value); useEffect(() => { const t = setTimeout(() => setDeb(value), delay); return () => clearTimeout(t); }, [value, delay]); return deb; }
const digitsOnly = (s) => Array.from(String(s || "")).filter(ch => ch >= '0' && ch <= '9').join("");

// Normaliza a E.164 sin "+" (AR heurística).
function normalizePhoneE164(raw) {
    let d = digitsOnly(raw);
    if (!d) return "";
    if (d.startsWith("549")) return d;
    if (d.startsWith("54") && !d.startsWith("549")) return "549" + d.slice(2);
    if (d.startsWith("0")) d = d.slice(1);
    if (d.length >= 10 && d.length <= 12) return "549" + d;
    return d;
}

/* ================================
   Intentos de búsqueda “inteligente”
================================ */
const SEARCH_INTENTS = [
    {
        match: (q) => /enamorad|san\s*valent|amor|corazon(es)?/i.test(q),
        apply: (p) => /corazon|love|amor|heart/i.test(p?.name || "") || /corazon|heart/i.test(p?.features || "")
    },
    {
        match: (q) => /oficina|office|trabajo|escuela|colegio|uni|facultad/i.test(q),
        apply: (p) => /mochila|lunchera|cartuchera|bolso|notebook|organizador/i.test(p?.name || "") || /OSLO/i.test(p?.marca || "")
    },
];

/* ================================
   Catálogo
================================ */
export default function Catalogo({ collectionName = DEFAULT_COLLECTION, showroom = false }) {
    const params = useParams();
    const location = useLocation();
    // Detecta showroom si la URL contiene /catalogo/showroom/ aunque no llegue el prop
    const isShowroomRoute = showroom || (location?.pathname || "").includes("/catalogo/showroom/");
    const [sessionId] = useState(() => crypto?.randomUUID?.() || String(Date.now()));
    const phoneParam = params?.numeroTelefono || params?.numeroComoAntes || "";
    const phoneE164 = normalizePhoneE164(phoneParam);

    // cliente
    const [client, setClient] = useState(null);
    const [needsOnboarding, setNeedsOnboarding] = useState(false);
    const [rz, setRz] = useState("");
    const [cuit, setCuit] = useState("");

    // datos productos
    const [itemsAll, setItemsAll] = useState([]);
    const [loadedAll, setLoadedAll] = useState(false);
    const [loadingAll, setLoadingAll] = useState(false);

    // últimas compras
    const [lastOrders, setLastOrders] = useState([]);

    // UI
    const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
    const [filters, setFilters] = useState({ linea: new Set(), marca: new Set(), tipo: new Set() });
    const [sort, setSort] = useState("relevance");
    const [selected, setSelected] = useState(null);
    const [view, setView] = useState("grid2");

    const [showFilters, setShowFilters] = useState(false);
    const [showSort, setShowSort] = useState(false);

    // 🔸 NUEVO: flujo de selección
    const [mode, setMode] = useState(null); // null | "manual" | "ia" | "recomprar"
    const [manualStep, setManualStep] = useState("banners"); // "banners" -> "list" al elegir

    // búsqueda + voz
    const [search, setSearch] = useState("");
    const debouncedSearch = useDebounced(search, 250);
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef(null);

    // carrito
    const [cartOpen, setCartOpen] = useState(false);
    const [cart, setCart] = useState(() => { try { return JSON.parse(localStorage.getItem("smartcart_v3") || "{}"); } catch { return {}; } });
    useEffect(() => { localStorage.setItem("smartcart_v3", JSON.stringify(cart)); }, [cart]);
    const [showCheckout, setShowCheckout] = useState(false);
    const [placing, setPlacing] = useState(false);
    const [orderId, setOrderId] = useState(null);
    const [lastOrderSnapshot, setLastOrderSnapshot] = useState(null);


    // Showroom intro (abre si es ruta showroom via prop o via URL)
    const [showShowroomIntro, setShowShowroomIntro] = useState(() => !!isShowroomRoute);
    const [arrival, setArrival] = useState("");     // "bien", "con_dificultad", "otra"
    const [arrivalNotes, setArrivalNotes] = useState("");
    const [beverage, setBeverage] = useState("");   // "cafe", "agua", "te", "nada"


    // Escáner
    const [scannerOpen, setScannerOpen] = useState(false);
    const [manualBarcode, setManualBarcode] = useState("");

    // Feedback visual de agregado por escaneo
    const [scanAdded, setScanAdded] = useState(null); // { name, sku } | null


    function normalizeBarcode(raw) {
        return String(raw || "").replace(/\D/g, "");
    }

    function findProductByBarcode(code) {
        const c = normalizeBarcode(code);
        if (!c) return null;

        // 1) match directo en memoria (itemsAll)
        const keys = ["barcode", "ean", "ean13", "ean_13", "sku"];
        const hit = itemsAll.find(p => keys.some(k => normalizeBarcode(p?.[k]) === c));
        if (hit) return hit;

        // 2) algunos EAN-13 tienen leading zeros: probá variantes
        if (c.length === 12) {
            const hit12 = itemsAll.find(p => keys.some(k => normalizeBarcode(p?.[k]) === "0" + c));
            if (hit12) return hit12;
        }
        if (c.length === 13 && c.startsWith("0")) {
            const hit13 = itemsAll.find(p => keys.some(k => normalizeBarcode(p?.[k]) === c.slice(1)));
            if (hit13) return hit13;
        }

        return null;
    }

    function playBeep(type = "ok") {
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.type = "sine";
            // dos tonos: ok alto y breve; err bajo y un poco más largo
            const freq = type === "ok" ? 880 : 440;
            const dur = type === "ok" ? 0.12 : 0.25;
            o.frequency.setValueAtTime(freq, ctx.currentTime);
            g.gain.setValueAtTime(0.0001, ctx.currentTime);
            g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
            g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
            o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + dur);
        } catch (_) {
            // Fallback súper simple
            try { new Audio("data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABYAAABkAAA=").play(); } catch { }
        }
    }


    function normalizeBarcode(raw) {
        return String(raw || "").replace(/\D/g, "");
    }

    function findProductByBarcode(code) {
        const c = normalizeBarcode(code);
        if (!c) return null;

        const keys = ["barcode", "ean", "ean13", "ean_13", "sku"];
        const hit = itemsAll.find(p => keys.some(k => normalizeBarcode(p?.[k]) === c));
        if (hit) return hit;

        if (c.length === 12) {
            const hit12 = itemsAll.find(p => keys.some(k => normalizeBarcode(p?.[k]) === "0" + c));
            if (hit12) return hit12;
        }
        if (c.length === 13 && c.startsWith("0")) {
            const hit13 = itemsAll.find(p => keys.some(k => normalizeBarcode(p?.[k]) === c.slice(1)));
            if (hit13) return hit13;
        }
        return null;
    }

    async function handleBarcodeDetected(code) {
        const p = findProductByBarcode(code);
        if (p && Number(p.stock || 0) > 0) {
            addToCart(p, 1);
            try { window.navigator.vibrate?.(40); } catch { }
            playBeep("ok");
            setScanAdded({ name: p.name, sku: p.sku });
            // auto-hide del badge a los 1.2s
            setTimeout(() => setScanAdded(null), 1200);
        } else {
            playBeep("err");
            alert(`No encontramos producto con código: ${code}`);
        }
    }



    async function submitShowroomIntro() {
        if (!arrival || !beverage) {
            alert("Elegí cómo te fue con la llegada y tu preferencia de bebida.");
            return;
        }
        // Guardamos registro de visita showroom
        try {
            await addDoc(collection(db, "showroomVisits"), {
                sessionId,
                phoneE164: phoneE164 || null,
                arrival, arrivalNotes: arrivalNotes || null,
                beverage,
                ts: Date.now(),
                createdAt: serverTimestamp(),
            });
            await trackEvent({
                type: "showroom_intro",
                phoneE164: phoneE164 || null,
                payload: { arrival, beverage, hasNotes: !!arrivalNotes }
            });
        } catch (e) { /* noop */ }
        setShowShowroomIntro(false);
    }


    /* Buscar cliente por teléfono */
    useEffect(() => {
        (async () => {
            if (!phoneE164) {
                setNeedsOnboarding(true); // sin número -> pedir datos
                return;
            }
            await trackEvent({ type: "visit", phoneE164 });
            const q1 = query(collection(db, "clients"), where("phoneE164", "==", phoneE164));
            const snap = await getDocs(q1);
            if (snap.empty) {
                setNeedsOnboarding(true);
            } else {
                const c = { id: snap.docs[0].id, ...snap.docs[0].data() };
                setClient(c);
                setRz(c.razonSocial || "");
                setCuit(c.cuit || "");
            }
        })();
    }, [phoneE164]);

    /* Últimas compras */
    useEffect(() => {
        (async () => {
            if (!client?.cuit) { setLastOrders([]); return; }
            const q2 = query(collection(db, "orders"), where("cuit", "==", client.cuit), orderBy("createdAt", "desc"), limit(10));
            const snap = await getDocs(q2);
            setLastOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        })();
    }, [client?.cuit]);

    /* Speech */
    useEffect(() => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) return;
        const rec = new SR();
        rec.lang = "es-AR";
        rec.interimResults = true;
        rec.continuous = false;
        rec.onresult = (e) => {
            let finalTxt = "";
            for (let i = e.resultIndex; i < e.results.length; i++) finalTxt += e.results[i][0].transcript;
            setSearch(s => (finalTxt?.length ? finalTxt : s));
        };
        rec.onend = () => setIsListening(false);
        recognitionRef.current = rec;
    }, []);
    const toggleListen = () => { const rec = recognitionRef.current; if (!rec) return; if (!isListening) { setIsListening(true); rec.start(); } else { rec.stop(); setIsListening(false); } };

    /* Onboarding */
    async function saveOnboarding() {
        const rzClean = String(rz || "").trim();
        const cuitClean = digitsOnly(cuit);
        if (!rzClean && cuitClean.length !== 11) { alert("Ingresá Razón Social o CUIT/CUIL."); return; }
        const payload = { phoneE164: phoneE164 || null, razonSocial: rzClean || null, cuit: cuitClean || null, createdAt: serverTimestamp(), lastOrderAt: null };
        const ref = doc(collection(db, "clients"));
        await setDoc(ref, payload);
        const data = { id: ref.id, ...payload };
        setClient(data);
        setNeedsOnboarding(false);
        await trackEvent({ type: "onboarding", phoneE164: phoneE164 || null, payload: { razonSocial: !!rzClean, cuit: !!cuitClean } });
    }

    const greeting = client?.razonSocial ? `¡Hola ${client.razonSocial}!` : "¡Hola!";

    /* Cargar catálogo */
    useEffect(() => {
        (async () => {
            setLoadingAll(true);
            try {
                const all = []; let cursor = null;
                while (true) {
                    const parts = [collection(db, collectionName), orderBy("name"), limit(FETCH_PAGE)];
                    if (cursor) parts.push(startAfter(cursor));
                    const q = query(...parts);
                    const snap = await getDocs(q);
                    const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                    all.push(...rows);
                    if (snap.size < FETCH_PAGE) break;
                    cursor = snap.docs[snap.docs.length - 1];
                }
                setItemsAll(all);
            } catch (e) { console.error("Error loading products", e); }
            finally { setLoadingAll(false); setLoadedAll(true); }
        })();
    }, [collectionName]);

    /* Facetas + filtro inteligente */
    const facets = useMemo(() => {
        const linea = new Set(), marca = new Set(), tipo = new Set();
        for (const p of itemsAll) {
            if (p.linea && !isNA(p.linea)) linea.add(p.linea);
            if (p.marca && !isNA(p.marca)) marca.add(p.marca);
            if (p.tipo && !isNA(p.tipo)) tipo.add(p.tipo);
        }
        return { linea: [...linea].sort(), marca: [...marca].sort(), tipo: [...tipo].sort() };
    }, [itemsAll]);

    function passesIntent(term, p) {
        for (const intent of SEARCH_INTENTS) {
            if (intent.match(term)) return !!intent.apply(p);
        }
        return null;
    }

    const filtered = useMemo(() => {
        const term = safeStr(debouncedSearch);
        let list = itemsAll.filter(p => {
            if (!(Number(p.stock || 0) > 0)) return false;
            if (filters.linea.size && !filters.linea.has(p.linea)) return false;
            if (filters.marca.size && !filters.marca.has(p.marca)) return false;
            if (filters.tipo.size && !filters.tipo.has(p.tipo)) return false;
            if (!term) return true;
            const intentOk = passesIntent(term, p);
            if (intentOk !== null) return intentOk;
            const hay = [p.name, p.sku, isNA(p.marca) ? "" : p.marca, isNA(p.linea) ? "" : p.linea, isNA(p.tipo) ? "" : p.tipo, p.subtipo, p.modelo].map(safeStr).join(" ");
            return hay.includes(term);
        });
        switch (sort) {
            case "price_asc": list.sort((a, b) => (a.price || 0) - (b.price || 0)); break;
            case "price_desc": list.sort((a, b) => (b.price || 0) - (a.price || 0)); break;
            case "name_az": list.sort((a, b) => safeStr(a.name).localeCompare(safeStr(b.name))); break;
            case "name_za": list.sort((a, b) => safeStr(b.name).localeCompare(safeStr(a.name))); break;
            default: break;
        }
        return list;
    }, [itemsAll, filters, debouncedSearch, sort]);

    const visible = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
    const canLoadMore = visibleCount < filtered.length;

    /* Recomprar y Sugerencias IA */
    const last10Items = useMemo(() => {
        const acc = new Map();
        for (const o of lastOrders) for (const it of (o.items || [])) {
            if (!acc.has(it.sku)) acc.set(it.sku, { ...it, totalQty: 0 });
            acc.get(it.sku).totalQty += Number(it.qty || 0);
        }
        return Array.from(acc.values()).slice(0, 10);
    }, [lastOrders]);

    const last10WithStock = useMemo(() => {
        const bySku = new Map(itemsAll.map(p => [p.sku, p]));
        return last10Items
            .map(it => ({ ...bySku.get(it.sku), prevQty: it.totalQty }))
            .filter(p => p && Number(p.stock || 0) > 0)
            .slice(0, 10);
    }, [itemsAll, last10Items]);

    const liked = useMemo(() => {
        const brandCounts = new Map(); const catCounts = new Map();
        for (const it of last10Items) {
            const b = (it.brand || it.marca || "").toUpperCase();
            const c = (it.category || it.linea || it.tipo || "").toUpperCase();
            if (b) brandCounts.set(b, (brandCounts.get(b) || 0) + 1);
            if (c) catCounts.set(c, (catCounts.get(c) || 0) + 1);
        }
        return { brandCounts, catCounts };
    }, [last10Items]);

    const iaSuggestions = useMemo(() => {
        if (!itemsAll.length) return [];
        const { brandCounts, catCounts } = liked;
        const avg = (last10Items.reduce((a, i) => a + (Number(i.price || 0)), 0) / Math.max(1, last10Items.length)) || 0;
        const score = (p) => {
            let s = 0;
            const b = (p.marca || "").toUpperCase();
            const cands = [p.linea, p.tipo, p.subtipo].map(x => (x || "").toUpperCase());
            if (brandCounts.has(b)) s += 3 * brandCounts.get(b);
            for (const c of cands) if (catCounts.has(c)) s += 2 * catCounts.get(c);
            const dp = Math.abs((Number(p.price || 0)) - avg);
            s += Math.max(0, 2 - (avg ? dp / avg : 0));
            return s;
        };
        return itemsAll
            .filter(p => Number(p.stock || 0) > 0)
            .map(p => ({ p, s: score(p) }))
            .sort((a, b) => b.s - a.s)
            .slice(0, 20)
            .map(x => x.p);
    }, [itemsAll, liked, last10Items]);

    /* Carrito */
    const cartCount = useMemo(() => Object.values(cart).reduce((a, it) => a + it.qty, 0), [cart]);
    const cartTotal = useMemo(() => Object.values(cart).reduce((a, it) => a + it.qty * (it.price || 0), 0), [cart]);

    function addToCart(p, qty = 1, e) {
        if (e) e.stopPropagation(); if (!p || (p.stock ?? 0) <= 0) return;
        setCart(prev => { const next = { ...prev }; const ex = next[p.sku]; const maxQty = Math.max(0, Number(p.stock || 0)); const newQty = Math.min(maxQty, (ex?.qty || 0) + qty); next[p.sku] = { sku: p.sku, name: p.name, price: Number(p.price || 0), imageUrl: p.imageUrl || null, stock: Number(p.stock || 0), qty: newQty }; return next; });
        setCartOpen(true);
        trackEvent({ type: "cart_add", phoneE164: phoneE164 || null, payload: { sku: p.sku, qty } });
    }
    const updateCartQty = (sku, qty) => setCart(prev => { const n = { ...prev }; const ex = n[sku]; if (!ex) return prev; const q = Math.max(0, Math.min(ex.stock, qty)); if (q === 0) delete n[sku]; else n[sku] = { ...ex, qty: q }; return n; });
    const removeFromCart = (sku) => { setCart(prev => { const n = { ...prev }; delete n[sku]; return n; }); trackEvent({ type: "cart_remove", phoneE164: phoneE164 || null, payload: { sku } }); };
    const clearCart = () => { setCart({}); trackEvent({ type: "cart_clear", phoneE164: phoneE164 || null }); };
    function startCheckout() { if (cartTotal >= MIN_PURCHASE) setShowCheckout(true); trackEvent({ type: "checkout_open", phoneE164: phoneE164 || null, payload: { total: cartTotal } }); }

    async function placeOrder() {
        const cuitClean = digitsOnly(cuit || client?.cuit || "");
        if (cartTotal < MIN_PURCHASE) { alert(`La compra mínima es ${ARS.format(MIN_PURCHASE)}.`); return; }
        if (cuitClean.length < 11) { alert("Revisá el CUIT/CUIL (11 dígitos)."); return; }
        setPlacing(true);
        try {
            const items = Object.values(cart).map(it => ({ sku: it.sku, name: it.name, qty: it.qty, price: it.price, subtotal: it.qty * it.price }));
            const order = { items, total: cartTotal, cuit: cuitClean, phoneE164: phoneE164 || null, status: "pending", source: "web-catalog", createdAt: serverTimestamp() };
            const ref = await addDoc(collection(db, "webOrders"), order);
            setLastOrderSnapshot({ id: ref.id, items, total: cartTotal, cuit: cuitClean, phone: phoneE164 || "" });
            setOrderId(ref.id); setShowCheckout(false); setCart({}); await trackEvent({ type: "order_created", phoneE164: phoneE164 || null, payload: { orderId: ref.id, total: cartTotal } });
        } catch (e) { console.error("placeOrder error", e); alert("No pudimos confirmar el pedido. Intentá de nuevo."); }
        finally { setPlacing(false); }
    }

    function sendWhatsAppReceipt() {
        const o = lastOrderSnapshot; if (!o) { alert("No hay un pedido confirmado para enviar."); return; }
        if (!WA_DESTINATION || !/^\d{8,}$/.test(WA_DESTINATION)) { alert("Configurá WA_DESTINATION válido."); return; }
        const topLines = o.items.slice(0, 12).map(it => `• ${it.name} (SKU ${it.sku}) x${it.qty} — ${ARS.format(it.price)} c/u = ${ARS.format(it.subtotal)}`).join("\n");
        const extra = o.items.length > 12 ? `\n… y ${o.items.length - 12} ítems más` : "";
        const msg = `Hola!\nEnvío comprobante del pedido.\n\nCUIT: ${o.cuit}\nTel: ${o.phone}\nResumen:\n${topLines}${extra}\n\nTOTAL: ${ARS.format(o.total)}\nOrigen: WhatsApp`;
        const waURL = `https://wa.me/${WA_DESTINATION}?text=${encodeURIComponent(msg)}`;
        window.open(waURL, "_blank", "noopener,noreferrer");
    }

    /* Tracking */
    async function trackEvent({ type, phoneE164, payload }) {
        try {
            await addDoc(collection(db, "catalogEvents"), {
                type, phoneE164: phoneE164 || null, sessionId,
                cuit: client?.cuit || null, ts: Date.now(), payload: payload || null,
            });
        } catch (e) { /* noop */ }
    }
    useEffect(() => { if (debouncedSearch) trackEvent({ type: "search", phoneE164: phoneE164 || null, payload: { q: debouncedSearch } }); }, [debouncedSearch]);

    /* Helpers de filtros */
    const activeFilterCount = (filters.linea.size + filters.marca.size + filters.tipo.size);
    const removeTag = (group, value) => { const next = new Set(filters[group]); next.delete(value); setFilters({ ...filters, [group]: next }); };
    const clearAllFilters = () => setFilters({ linea: new Set(), marca: new Set(), tipo: new Set() });

    /* Acciones banners -> solo cuando mode === "manual" y manualStep === "banners" */
    const handleBannerPick = (banner) => {
        const { group, value } = banner;
        const current = new Set(filters[group]);
        current.add(value);
        setFilters({ ...filters, [group]: current });
        setManualStep("list");
        trackEvent({ type: "banner_click", phoneE164: phoneE164 || null, payload: { group, value } });
    };
    const handleShowAll = () => {
        clearAllFilters();
        setManualStep("list");
        window.scrollTo({ top: 0, behavior: "smooth" });
        trackEvent({ type: "view_all", phoneE164: phoneE164 || null });
    };

    /* ==== RENDER ==== */
    const showRecomprarOption = !!(client?.cuit && !needsOnboarding); // ocultar si no hay cliente identificado

    return (
        <div className="min-h-screen bg-white text-slate-900">
            {/* Header */}
            <header className="sticky top-0 z-40 backdrop-blur bg-white/80 border-b border-slate-200">
                <div className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6 py-2 sm:py-3 flex items-center gap-2 sm:gap-3">
                    <div className="flex items-center gap-2 sm:gap-3">
                        <img src="../logo.png" alt="Charko" className="h-8 sm:h-9 w-auto select-none" draggable="false" />
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        <button onClick={() => setShowFilters(true)} className={cx("h-10 px-3 rounded-xl bg-white border border-slate-300 hover:bg-slate-50 text-sm inline-flex items-center gap-2", activeFilterCount > 0 && "relative")} aria-label="Filtros">
                            <Filter className="h-4 w-4" /><span className="hidden sm:inline">Filtros</span>
                            {activeFilterCount > 0 && (<span className="ml-1 text-[11px] px-1.5 py-0.5 rounded-full bg-red-600 text-white">{activeFilterCount}</span>)}
                        </button>
                        <button onClick={() => setShowSort(true)} className="h-10 px-3 rounded-xl bg-white border border-slate-300 hover:bg-slate-50 text-sm inline-flex items-center gap-2" aria-label="Ordenar">
                            <SlidersHorizontal className="h-4 w-4" /><span className="hidden sm:inline">Ordenar</span>
                        </button>
                        <button onClick={() => setView(v => (v === "list" ? "grid2" : "list"))} className="h-10 px-3 rounded-xl bg-white border border-slate-300 hover:bg-slate-50 text-sm inline-flex items-center gap-2" title="Cambiar vista">
                            {view === "list" ? <LayoutGrid className="h-4 w-4" /> : <ListIcon className="h-4 w-4" />}<span className="hidden sm:inline">{view === "list" ? "Cuadrícula" : "Lista"}</span>
                        </button>
                        <button onClick={() => setCartOpen(true)} className="h-10 px-3 rounded-xl bg-red-600 hover:bg-red-500 text-white inline-flex items-center">
                            <ShoppingCart className="h-4 w-4 mr-1.5" /><span className="hidden sm:inline">Carrito</span>
                            <span className="ml-1 text-xs px-1.5 py-0.5 rounded-full bg-white/20 text-white">{cartCount}</span>
                        </button>

                        <button
                            onClick={() => setScannerOpen(true)}
                            className="h-10 px-3 rounded-xl bg-white border border-slate-300 hover:bg-slate-50 text-sm inline-flex items-center gap-2"
                            title="Escanear código de barras"
                        >
                            <span className="hidden sm:inline">Escanear</span>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path d="M3 6v12M7 6v12M11 6v12M13 6v12M17 6v12M21 6v12" />
                            </svg>
                        </button>

                    </div>
                </div>
            </header>

            {/* 🔸 Modal Showroom: preguntas iniciales */}
            <AnimatePresence>
                {showShowroomIntro && (
                    <motion.div className="fixed inset-0 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <div className="absolute inset-0 bg-black/40" />
                        <motion.div
                            initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
                            transition={{ type: "spring", stiffness: 260, damping: 22 }}
                            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[96vw] max-w-xl bg-white border border-slate-200 rounded-2xl shadow-xl max-h-[85vh] overflow-y-auto p-4 sm:p-5"
                        >
                            <div className="flex items-start justify-between">
                                <div>
                                    <h3 className="text-lg sm:text-xl font-semibold">¡Bienvenido al showroom!</h3>
                                    <p className="text-sm text-slate-600">Antes de empezar, contanos:</p>
                                </div>
                                <button onClick={() => setShowShowroomIntro(false)} className="h-9 w-9 inline-flex items-center justify-center rounded-xl bg-white border border-slate-300 hover:bg-slate-50">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>

                            <div className="mt-3 space-y-4">
                                <div>
                                    <p className="text-sm font-medium">1) ¿Cómo te fue para llegar al showroom?</p>
                                    <div className="mt-2 grid grid-cols-1 gap-2">
                                        <label className={cx("flex items-center gap-2 px-3 py-2 rounded-xl border", arrival === "bien" ? "border-emerald-300 bg-emerald-50" : "border-slate-200")}>
                                            <input type="radio" name="arrival" value="bien" checked={arrival === "bien"} onChange={() => setArrival("bien")} className="accent-emerald-600" />
                                            <span>Llegué bien</span>
                                        </label>
                                        <label className={cx("flex items-center gap-2 px-3 py-2 rounded-xl border", arrival === "con_dificultad" ? "border-amber-300 bg-amber-50" : "border-slate-200")}>
                                            <input type="radio" name="arrival" value="con_dificultad" checked={arrival === "con_dificultad"} onChange={() => setArrival("con_dificultad")} className="accent-amber-600" />
                                            <span>Tuve alguna dificultad</span>
                                        </label>
                                        <label className={cx("flex items-center gap-2 px-3 py-2 rounded-xl border", arrival === "otra" ? "border-slate-300 bg-slate-50" : "border-slate-200")}>
                                            <input type="radio" name="arrival" value="otra" checked={arrival === "otra"} onChange={() => setArrival("otra")} className="accent-slate-600" />
                                            <span>Otro</span>
                                        </label>
                                        <textarea
                                            placeholder="(Opcional) Dejanos un comentario para mejorar indicaciones, estacionamiento, etc."
                                            value={arrivalNotes}
                                            onChange={e => setArrivalNotes(e.target.value)}
                                            className="mt-1 w-full h-24 rounded-xl bg-white border border-slate-300 px-3 py-2 outline-none focus:ring-2 focus:ring-red-500 text-sm"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <p className="text-sm font-medium">2) ¿Querés tomar algo?</p>
                                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                                        {[
                                            ["cafe", "Café"],
                                            ["agua", "Agua"],
                                            ["te", "Té"],
                                            ["nada", "Nada por ahora"]
                                        ].map(([val, label]) => (
                                            <label key={val} className={cx("flex items-center gap-2 px-3 py-2 rounded-xl border", beverage === val ? "border-red-300 bg-red-50" : "border-slate-200")}>
                                                <input type="radio" name="beverage" value={val} checked={beverage === val} onChange={() => setBeverage(val)} className="accent-red-600" />
                                                <span>{label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4 flex justify-end gap-2">
                                <button onClick={() => setShowShowroomIntro(false)} className="h-10 px-4 rounded-xl bg-white border border-slate-300 hover:bg-slate-50">Omitir</button>
                                <button onClick={submitShowroomIntro} className="h-10 px-4 rounded-xl bg-red-600 hover:bg-red-500 text-white">Continuar</button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>


            {/* 🔸 Primera pantalla: selección de modo (SIEMPRE primero) */}
            {!mode && (
                <section className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6 pt-6 pb-4">
                    <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-6 md:flex md:items-center md:gap-6">
                        {/* Columna izquierda: texto + modos */}
                        <div className="flex-1">
                            <h2 className="text-lg sm:text-xl font-semibold">{greeting}</h2>
                            <p className="text-sm text-slate-600 mt-1">¿Cómo querés empezar?</p>

                            {/* Onboarding (si no hay cliente); no habilita Recomprar */}
                            {needsOnboarding && (
                                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <label className="text-sm sm:col-span-1">
                                        Razón Social
                                        <input
                                            value={rz}
                                            onChange={e => setRz(e.target.value)}
                                            placeholder="Tu comercio"
                                            className="mt-1 w-full h-11 rounded-xl bg-white border border-slate-300 px-3 outline-none focus:ring-2 focus:ring-red-500"
                                        />
                                    </label>
                                    <label className="text-sm sm:col-span-1">
                                        CUIT/CUIL
                                        <input
                                            value={cuit}
                                            onChange={e => setCuit(e.target.value)}
                                            placeholder="11 dígitos"
                                            className="mt-1 w-full h-11 rounded-xl bg-white border border-slate-300 px-3 outline-none focus:ring-2 focus:ring-red-500"
                                        />
                                    </label>
                                    <div className="flex items-end sm:col-span-1">
                                        <button
                                            onClick={saveOnboarding}
                                            className="h-11 px-4 rounded-xl bg-red-600 hover:bg-red-500 text-white w-full"
                                        >
                                            Guardar
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                                {/* Recomprar: mostrar SOLO si hay cliente identificado */}
                                {showRecomprarOption && (
                                    <ModeCard
                                        label="Recomprar"
                                        icon={<Clock className="h-5 w-5" />}
                                        onClick={() => {
                                            setMode("recomprar");
                                            trackEvent({
                                                type: "mode",
                                                phoneE164: phoneE164 || null,
                                                payload: { mode: "recomprar" }
                                            });
                                        }}
                                    />
                                )}
                                <ModeCard
                                    label="Sugerencias IA"
                                    highlight
                                    icon={<Sparkles className="h-5 w-5" />}
                                    onClick={() => {
                                        setMode("ia");
                                        trackEvent({
                                            type: "mode",
                                            phoneE164: phoneE164 || null,
                                            payload: { mode: "ia" }
                                        });
                                    }}
                                />
                                <ModeCard
                                    label="Búsqueda manual"
                                    icon={<Search className="h-5 w-5" />}
                                    onClick={() => {
                                        setMode("manual");
                                        setManualStep("banners");
                                        trackEvent({
                                            type: "mode",
                                            phoneE164: phoneE164 || null,
                                            payload: { mode: "manual" }
                                        });
                                    }}
                                />
                            </div>
                        </div>

                        {/* Columna derecha: GIF animado */}
                        <div className="hidden md:flex md:flex-col md:items-center md:justify-center w-40 shrink-0">
                            <img
                                src="/patricio-estrella.gif"
                                alt="Patricio celebrando"
                                className="w-full h-auto pointer-events-none select-none"
                            />
                        </div>
                    </div>
                </section>
            )}


            {/* 🔸 MODO: Recomprar (lista directa) */}
            {mode === "recomprar" && (
                <section className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6 pt-4">
                    <div className="flex items-center gap-2 mb-2"><Clock className="h-5 w-5" /><h3 className="text-lg font-semibold">Tus últimos productos (con stock)</h3></div>
                    <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4">
                        {last10WithStock.map(p => (
                            <CardTile key={p.sku} p={p} hero={false} onOpen={() => setSelected(p)} onAdd={(e) => addToCart(p, 1, e)} />
                        ))}
                    </div>
                </section>
            )}

            {/* 🔸 MODO: Sugerencias IA (lista directa) */}
            {mode === "ia" && (
                <section className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6 pt-4">
                    <div className="flex items-center gap-2"><Sparkles className="h-5 w-5" /><h3 className="text-lg font-semibold">Recomendado para vos</h3></div>
                    <p className="text-slate-600 text-sm mb-2">Basado en tus compras por marca y categoría.</p>
                    <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4">
                        {iaSuggestions.map(p => (
                            <CardTile key={p.sku} p={p} hero={false} onOpen={() => setSelected(p)} onAdd={(e) => addToCart(p, 1, e)} />
                        ))}
                    </div>
                </section>
            )}

            {/* 🔸 MODO: Manual -> PRIMERO BANNERS, luego listado */}
            {mode === "manual" && manualStep === "banners" && (
                <section className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6 pt-4 pb-4">
                    <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-6">
                        <h3 className="text-lg font-medium">Elegí por marca</h3>
                        <p className="mt-1 text-slate-600 text-sm">Seleccioná una opción y después podés filtrar y ordenar.</p>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                            {BANNERS.map(b => (
                                <button key={b.label} onClick={() => handleBannerPick(b)} className="relative group h-32 sm:h-36 rounded-2xl overflow-hidden border border-slate-200 hover:shadow-md transition" title={b.label}>
                                    <img src={b.img} alt={b.label} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                                </button>
                            ))}
                        </div>
                        <div className="mt-3">
                            <button onClick={handleShowAll} className="w-full h-12 sm:h-10 rounded-2xl bg-red-600 hover:bg-red-500 text-white">Ver todo el catálogo</button>
                        </div>
                    </div>
                </section>
            )}

            {/* Listado manual luego de banners */}
            {mode === "manual" && manualStep === "list" && (
                <main className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6 pb-28 sm:pb-24">
                    {!loadedAll && loadingAll && (<Loader />)}
                    {visible.length === 0 && loadedAll && !loadingAll && (<EmptyState title="Sin resultados" subtitle="Ajustá filtros o la búsqueda." />)}
                    <motion.div layout className={cx(view === "grid2" && "grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4", view === "list" && "grid grid-cols-1 xs:grid-cols-2 gap-3", view === "hero1" && "grid grid-cols-1 gap-3 sm:gap-4")}>
                        <AnimatePresence>
                            {visible.map(p => (
                                <motion.div key={p.sku} layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.25 }}>
                                    {view === "list"
                                        ? <CardRow p={p} onOpen={() => { setSelected(p); trackEvent({ type: "product_view", phoneE164: phoneE164 || null, payload: { sku: p.sku } }); }} onAdd={(e) => addToCart(p, 1, e)} />
                                        : <CardTile p={p} hero={view === "hero1"} onOpen={() => { setSelected(p); trackEvent({ type: "product_view", phoneE164: phoneE164 || null, payload: { sku: p.sku } }); }} onAdd={(e) => addToCart(p, 1, e)} />
                                    }
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </motion.div>
                    <div className="mt-6 sm:mt-8 flex justify-center">
                        <button onClick={() => setVisibleCount(v => v + LOAD_MORE_STEP)} disabled={!canLoadMore || loadingAll} className="min-w-[220px] h-10 rounded-xl px-4 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50">
                            {!loadedAll || loadingAll ? "Cargando…" : (canLoadMore ? "Cargar más" : "No hay más productos")}
                        </button>
                    </div>
                </main>
            )}

            {/* Modal Detalle */}
            <AnimatePresence>
                {selected && (
                    <motion.div className="fixed inset-0 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <div className="absolute inset-0 bg-black/40" onClick={() => setSelected(null)} />
                        <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }} transition={{ type: "spring", stiffness: 260, damping: 22 }} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[96vw] max-w-3xl bg-white border border-slate-200 rounded-2xl shadow-xl max-h-[85vh] overflow-y-auto p-4 sm:p-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                                <div className="sticky top-0 md:static">
                                    <img src={selected.imageUrl || ""} alt="" className="w-full h-auto rounded-xl bg-slate-100" />
                                </div>
                                <div>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <h2 className="text-xl sm:text-2xl font-semibold leading-tight line-clamp-3">{selected.name}</h2>
                                            <p className="text-slate-500 text-sm">SKU {selected.sku}</p>
                                        </div>
                                        <button onClick={() => setSelected(null)} className="h-9 w-9 inline-flex items-center justify-center rounded-xl bg-white border border-slate-300 hover:bg-slate-50"><X className="h-4 w-4" /></button>
                                    </div>
                                    <div className="mt-4 space-y-2">
                                        <p className="text-xl font-semibold">{ARS.format(Number(selected.price || 0))}</p>
                                        <div className="mt-3 text-sm text-slate-700 space-y-1">
                                            {!isNA(selected.marca) && selected.marca && <p><span className="text-slate-500">Marca:</span> {selected.marca}</p>}
                                            {!isNA(selected.linea) && selected.linea && <p><span className="text-slate-500">Línea:</span> {selected.linea}</p>}
                                            {!isNA(selected.tipo) && selected.tipo && <p><span className="text-slate-500">Tipo:</span> {selected.tipo}</p>}
                                            {!isNA(selected.propiedad) && selected.propiedad && <p><span className="text-slate-500">Propiedad:</span> {selected.propiedad}</p>}
                                            {!isNA(selected.subtipo) && selected.subtipo && <p><span className="text-slate-500">Subtipo:</span> {selected.subtipo}</p>}
                                            {!isNA(selected.modelo) && selected.modelo && <p><span className="text-slate-500">Modelo:</span> {selected.modelo}</p>}
                                            {!isNA(selected.unidad) && selected.unidad && <p><span className="text-slate-500">Unidad/Tamaño:</span> {selected.unidad}</p>}
                                            {!isNA(selected.features) && selected.features && <p><span className="text-slate-500">Características:</span> {selected.features}</p>}
                                        </div>
                                        <div className="pt-2 flex items-center gap-2">
                                            <button disabled={!(Number(selected.stock || 0) > 0)} onClick={(e) => addToCart(selected, 1, e)} className={cx("h-10 inline-flex items-center px-4 rounded-xl text-white", Number(selected.stock || 0) > 0 ? "bg-red-600 hover:bg-red-500" : "bg-red-300 cursor-not-allowed")}> <ShoppingCart className="h-4 w-4 mr-1" /> Agregar al carrito </button>
                                            <button onClick={() => setSelected(null)} className="h-10 inline-flex items-center px-4 rounded-xl bg-white border border-slate-300 hover:bg-slate-50">Cerrar</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Drawer Carrito + Checkout */}
            <AnimatePresence>
                {cartOpen && (
                    <>
                        <motion.div className="fixed inset-0 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                            <div className="absolute inset-0 bg-black/30" onClick={() => setCartOpen(false)} />
                        </motion.div>
                        <motion.aside initial={{ x: 640 }} animate={{ x: 0 }} exit={{ x: 640 }} transition={{ type: "spring", stiffness: 260, damping: 24 }} className="fixed right-0 top-0 bottom-0 z-50 w-full sm:w-[420px] md:w-[520px] bg-white text-slate-900 border-l border-slate-200 p-4 flex flex-col">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold">Carrito</h3>
                                <button onClick={() => setCartOpen(false)} className="h-9 w-9 inline-flex items-center justify-center rounded-xl bg-white border border-slate-300 hover:bg-slate-50"><X className="h-4 w-4" /></button>
                            </div>
                            <div className="mt-2 text-sm text-slate-600">Compra mínima: {ARS.format(MIN_PURCHASE)}</div>
                            {orderId && (
                                <div className="mt-3 p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800">
                                    <div className="flex items-start gap-2">
                                        <CheckCircle2 className="h-5 w-5 mt-0.5" />
                                        <div className="flex-1">
                                            <p className="font-medium">¡Pedido confirmado!</p>
                                            <p className="text-sm mt-0.5">Código: <span className="font-mono">{orderId}</span>.</p>
                                            <div className="mt-3"><button onClick={sendWhatsAppReceipt} className="h-10 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white">Enviar comprobante por WhatsApp</button></div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="mt-3 flex-1 overflow-y-auto pr-1 sm:pr-2 space-y-3">
                                {Object.values(cart).length === 0 && !orderId && <p className="text-sm text-slate-500">Tu carrito está vacío.</p>}
                                {Object.values(cart).map(it => (
                                    <div key={it.sku} className="bg-white border border-slate-200 rounded-2xl p-3 flex items-center gap-3 shadow-sm">
                                        <img src={it.imageUrl || ""} alt="" className="h-16 w-16 object-cover rounded-lg bg-slate-100" />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-3">
                                                <p className="font-medium truncate">{it.name}</p>
                                                <button onClick={() => removeFromCart(it.sku)} className="text-slate-500 hover:text-slate-800"><Trash2 className="h-4 w-4" /></button>
                                            </div>
                                            <p className="text-xs text-slate-500">SKU {it.sku}</p>
                                            <div className="mt-2 flex items-center gap-2 sm:gap-3">
                                                <div className="inline-flex items-center rounded-xl border border-slate-300">
                                                    <button onClick={() => updateCartQty(it.sku, Math.max(0, it.qty - 1))} className="px-2 py-1 text-slate-700 hover:bg-slate-50">-</button>
                                                    <span className="px-3 text-sm">{it.qty}</span>
                                                    <button onClick={() => updateCartQty(it.sku, Math.min(it.stock, it.qty + 1))} className="px-2 py-1 text-slate-700 hover:bg-slate-50">+</button>
                                                </div>
                                                <span className="ml-auto text-sm font-medium">{ARS.format(it.price * it.qty)}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-4 pt-3 border-t border-slate-200">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div>
                                        <p className="text-xs text-slate-500">Total</p>
                                        <p className="text-lg font-semibold">{ARS.format(cartTotal)}</p>
                                        {cartTotal < MIN_PURCHASE && (<p className="text-xs text-rose-600 mt-1">Te faltan {ARS.format(Math.max(0, MIN_PURCHASE - cartTotal))} para el mínimo.</p>)}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button onClick={clearCart} className="h-10 inline-flex items-center px-4 rounded-xl bg-white border border-slate-300 hover:bg-slate-50">Vaciar</button>
                                        <button onClick={startCheckout} disabled={cartTotal < MIN_PURCHASE} className={cx("h-10 inline-flex items-center px-4 rounded-xl text-white", cartTotal < MIN_PURCHASE ? "bg-red-300 cursor-not-allowed" : "bg-red-600 hover:bg-red-500")}>Avanzar</button>
                                    </div>
                                </div>

                                {showCheckout && !orderId && (
                                    <div className="mt-4 p-3 rounded-xl border border-slate-200">
                                        <p className="font-medium mb-2">Datos de facturación</p>
                                        <div className="grid grid-cols-1 gap-3">
                                            <label className="text-sm">CUIT/CUIL
                                                <input value={cuit} onChange={(e) => setCuit(e.target.value)} placeholder="11 dígitos" className="mt-1 w-full h-11 rounded-xl bg-white border border-slate-300 px-3 outline-none focus:ring-2 focus:ring-red-500" />
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => setShowCheckout(false)} className="h-11 inline-flex items-center px-4 rounded-xl bg-white border border-slate-300 hover:bg-slate-50">Cancelar</button>
                                                <button onClick={placeOrder} disabled={placing || cartTotal < MIN_PURCHASE} className={cx("h-11 inline-flex items-center px-4 rounded-xl text-white", (placing || cartTotal < MIN_PURCHASE) ? "bg-red-300" : "bg-red-600 hover:bg-red-500")}>{placing ? "Enviando…" : "Confirmar pedido"}</button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>

            {/* Scanner modal */}
            <AnimatePresence>
                {scannerOpen && (
                    <BarcodeScanner
                        onClose={() => setScannerOpen(false)}
                        onDetected={handleBarcodeDetected}
                        title="Escanear código para agregar al carrito"
                    />
                )}
            </AnimatePresence>

            {/* Badge flotante “Producto agregado” */}
            <AnimatePresence>
                {scanAdded && (
                    <motion.div
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 30, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 260, damping: 20 }}
                        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50"
                    >
                        <div className="px-4 py-2 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 shadow">
                            <div className="flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path d="M20 6L9 17l-5-5" />
                                </svg>
                                <span className="text-sm">
                                    Agregado: <b className="font-medium">{scanAdded.name}</b>
                                </span>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>



            {/* Popups */}
            <PopupFilters show={showFilters} onClose={() => setShowFilters(false)} facets={facets} filters={filters} setFilters={setFilters} clearAll={clearAllFilters} />
            <PopupSort show={showSort} onClose={() => setShowSort(false)} sort={sort} setSort={setSort} />

            {/* Buscador flotante */}
            <FloatingSearch search={search} setSearch={setSearch} isListening={isListening} toggleListen={toggleListen} />
        </div >
    );
}

/* ================================
   Subcomponentes
================================ */
function Loader() {
    return (
        <div className="py-16 grid place-items-center">
            <div className="h-10 w-10 rounded-full border-2 border-red-600 border-t-transparent animate-spin" />
            <p className="mt-3 text-sm text-slate-600">Cargando productos…</p>
        </div>
    );
}
function ModeCard({ label, icon, onClick, highlight }) {
    return (
        <button
            onClick={onClick}
            className={cx(
                "h-16 sm:h-14 rounded-2xl border flex items-center justify-center gap-2 px-3 transition",
                highlight
                    ? "border-amber-300 bg-gradient-to-r from-amber-50 to-amber-100 hover:shadow text-amber-900"
                    : "border-slate-200 hover:border-slate-300 hover:shadow-sm bg-white"
            )}
        >
            {icon}
            {label}
            {highlight && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-amber-500 text-white">Nuevo</span>}
        </button>
    );
}
function CardTile({ p, hero, onOpen, onAdd }) {
    return (
        <div onClick={onOpen} className="group bg-white border border-slate-200 overflow-hidden rounded-2xl hover:shadow-lg transition-all cursor-pointer">
            <div className={cx("relative overflow-hidden", hero ? "aspect-[16/9]" : "aspect-[4/3]")}>
                <img src={p.imageUrl || ""} alt={p.name || "Producto"} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04] bg-slate-100" loading="lazy" />
            </div>
            <div className="p-3 sm:p-4">
                <p className="text-base font-semibold line-clamp-2 leading-snug">{p.name}</p>
                <div className="mt-1 flex items-center justify-between">
                    <div>
                        <p className="text-xs text-slate-500">SKU {p.sku}</p>
                        <p className="mt-0.5 text-lg font-semibold">{ARS.format(Number(p.price || 0))}</p>
                    </div>
                    <button onClick={(e) => onAdd(e)} onMouseDown={(e) => e.stopPropagation()} className="h-9 inline-flex items-center px-3 rounded-xl bg-red-600 hover:bg-red-500 text-white">
                        <ShoppingCart className="h-4 w-4 mr-1" /> Agregar
                    </button>
                </div>
                <div className="mt-2 sm:mt-3 flex flex-wrap gap-2">
                    {!isNA(p.marca) && p.marca && <span className="text-[11px] px-2 py-0.5 rounded-lg bg-slate-100 border border-slate-200">{p.marca}</span>}
                    {!isNA(p.linea) && p.linea && <span className="text-[11px] px-2 py-0.5 rounded-lg bg-slate-100 border border-slate-200">{p.linea}</span>}
                    {!isNA(p.tipo) && p.tipo && <span className="text-[11px] px-2 py-0.5 rounded-lg bg-slate-100 border border-slate-200">{p.tipo}</span>}
                </div>
            </div>
        </div>
    );
}
function CardRow({ p, onOpen, onAdd }) {
    return (
        <div onClick={onOpen} className="group bg-white border border-slate-200 overflow-hidden rounded-2xl hover:shadow-md transition cursor-pointer p-3 flex items-center gap-3">
            <img src={p.imageUrl || ""} alt={p.name || "Producto"} className="h-24 w-24 object-cover rounded-xl bg-slate-100" />
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                    <p className="font-medium truncate pr-2 sm:pr-4">{p.name}</p>
                    <button onMouseDown={(e) => e.stopPropagation()} onClick={(e) => onAdd(e)} className="h-9 inline-flex items-center px-3 rounded-xl bg-red-600 hover:bg-red-500 text-white"><ShoppingCart className="h-4 w-4 mr-1" /> Agregar</button>
                </div>
                <p className="text-xs text-slate-500">SKU {p.sku}</p>
                <div className="mt-1 flex items-center gap-2 sm:gap-3 text-sm">
                    <span className="font-semibold">{ARS.format(Number(p.price || 0))}</span>
                    {!isNA(p.marca) && p.marca && <span className="text-[11px] px-2 py-0.5 rounded-lg bg-slate-100 border border-slate-200">{p.marca}</span>}
                    {!isNA(p.linea) && p.linea && <span className="text-[11px] px-2 py-0.5 rounded-lg bg-slate-100 border border-slate-200">{p.linea}</span>}
                    {!isNA(p.tipo) && p.tipo && <span className="text-[11px] px-2 py-0.5 rounded-lg bg-slate-100 border border-slate-200">{p.tipo}</span>}
                </div>
            </div>
        </div>
    );
}
function Tag({ label, onClear }) {
    return (
        <span className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded-lg bg-white border border-slate-300 whitespace-nowrap">
            {label}
            <button onClick={onClear} className="ml-1 h-4 w-4 grid place-items-center rounded hover:bg-slate-100"><X className="h-3 w-3" /></button>
        </span>
    );
}
function FacetSection({ label, opts = [], set, onToggle }) {
    return (
        <div>
            <p className="text-sm font-medium mb-2">{label}</p>
            <div className="grid grid-cols-1 gap-2">
                {opts.length === 0 && <p className="text-xs text-slate-500">No hay opciones.</p>}
                {opts.map(opt => (
                    <label key={opt} className={cx("flex items-center gap-2 px-2 py-1.5 rounded-lg border", set.has(opt) ? "bg-red-50 border-red-200" : "bg-white border-slate-200 hover:bg-slate-50")}>
                        <input type="checkbox" checked={set.has(opt)} onChange={() => onToggle(opt)} className="accent-red-600" />
                        <span className="text-sm truncate">{opt}</span>
                    </label>
                ))}
            </div>
        </div>
    );
}
function EmptyState({ title = "Nada por aquí", subtitle = "" }) {
    return (
        <div className="py-16 text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-white border border-slate-200 mb-4">
                <Search className="h-7 w-7 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold">{title}</h3>
            {subtitle && <p className="text-slate-500 mt-1">{subtitle}</p>}
        </div>
    );
}
function PopupFilters({ show, onClose, facets, filters, setFilters, clearAll }) {
    return (
        <AnimatePresence>
            {show && (
                <motion.div className="fixed inset-0 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <div className="absolute inset-0 bg-black/30" onClick={onClose} />
                    <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} transition={{ type: "spring", stiffness: 260, damping: 24 }} className="absolute left-1/2 top-[8%] -translate-x-1/2 w-[94vw] max-w-xl bg-white border border-slate-200 rounded-2xl p-4 shadow-xl">
                        <div className="flex items-center justify-between">
                            <p className="font-medium">Filtros</p>
                            <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-lg border border-slate-300 hover:bg-slate-50"><X className="h-4 w-4" /></button>
                        </div>
                        <div className="mt-3 grid grid-cols-1 gap-3 max-h-[60vh] overflow-y-auto pr-1">
                            <FacetSection label="Línea" opts={facets.linea} set={filters.linea} onToggle={(opt) => { const s = new Set(filters.linea); s.has(opt) ? s.delete(opt) : s.add(opt); setFilters({ ...filters, linea: s }); }} />
                            <FacetSection label="Marca" opts={facets.marca} set={filters.marca} onToggle={(opt) => { const s = new Set(filters.marca); s.has(opt) ? s.delete(opt) : s.add(opt); setFilters({ ...filters, marca: s }); }} />
                            <FacetSection label="Tipo" opts={facets.tipo} set={filters.tipo} onToggle={(opt) => { const s = new Set(filters.tipo); s.has(opt) ? s.delete(opt) : s.add(opt); setFilters({ ...filters, tipo: s }); }} />
                        </div>
                        <div className="mt-4 flex items-center justify-between">
                            <button onClick={clearAll} className="h-10 px-3 rounded-xl bg-white border border-slate-300 hover:bg-slate-50">Limpiar todo</button>
                            <button onClick={onClose} className="h-10 px-4 rounded-xl bg-red-600 hover:bg-red-500 text-white">Aplicar</button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
function PopupSort({ show, onClose, sort, setSort }) {
    return (
        <AnimatePresence>
            {show && (
                <motion.div className="fixed inset-0 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <div className="absolute inset-0 bg-black/30" onClick={onClose} />
                    <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} transition={{ type: "spring", stiffness: 260, damping: 24 }} className="absolute left-1/2 top-[12%] -translate-x-1/2 w-[92vw] max-w-sm bg-white border border-slate-200 rounded-2xl p-3 shadow-xl">
                        <div className="flex items-center justify-between mb-1">
                            <p className="font-medium">Ordenar</p>
                            <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-lg border border-slate-300 hover:bg-slate-50"><X className="h-4 w-4" /></button>
                        </div>
                        <div className="grid">
                            {[["relevance", "Relevancia"], ["price_asc", "Precio ↑"], ["price_desc", "Precio ↓"], ["name_az", "Nombre A→Z"], ["name_za", "Nombre Z→A"]].map(([val, label]) => (
                                <label key={val} className={cx("flex items-center gap-2 px-2 py-2 rounded-lg border mt-2", sort === val ? "bg-red-50 border-red-200" : "bg-white border-slate-200 hover:bg-slate-50")}>
                                    <input type="radio" name="sort" value={val} checked={sort === val} onChange={() => setSort(val)} className="accent-red-600" />
                                    <span className="text-sm">{label}</span>
                                </label>
                            ))}
                        </div>
                        <div className="mt-3 flex justify-end"><button onClick={onClose} className="h-9 px-4 rounded-xl bg-red-600 hover:bg-red-500 text-white">Aplicar</button></div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
function FloatingSearch({ search, setSearch, isListening, toggleListen }) {
    return (
        <div className="fixed left-0 right-0 bottom-0 z-40">
            <div className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6 pb-3 pt-2">
                <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="relative rounded-2xl border border-slate-300 bg-white shadow-lg flex items-center">
                    <Search className="absolute left-3 h-4 w-4 text-slate-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        inputMode="search"
                        placeholder='Buscar por nombre, SKU, marca, línea… o frases como "día de los enamorados" o "cosas de oficina"'
                        className="w-full pr-[92px] pl-9 rounded-2xl bg-white text-slate-900 placeholder:text-slate-400 outline-none h-12 text-[15px] sm:h-11 sm:text-sm"
                        style={{ paddingRight: 92 }}
                    />
                    {search?.length > 0 && (<button onClick={() => setSearch("")} className="absolute right-12 h-8 w-8 mr-8 grid place-items-center rounded-lg hover:bg-slate-100" title="Limpiar"><X className="h-4 w-4 text-slate-500" /></button>)}
                    <button onClick={toggleListen} className="absolute right-3 h-8 px-2 grid place-items-center rounded-lg hover:bg-slate-100" title={isListening ? "Detener voz" : "Buscar por voz"}>{isListening ? <MicOff className="h-4 w-4 text-red-600" /> : <Mic className="h-4 w-4 text-slate-600" />}</button>
                </motion.div>
            </div>
        </div>
    );
}



/* ================================
   CatalogAnalyticsPro
   Dashboard con gráficos
================================ */
export function CatalogAnalyticsPro() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const since = Date.now() - 30 * 24 * 60 * 60 * 1000; // 30 días
        const q1 = query(
            collection(db, "catalogEvents"),
            where("ts", ">=", since),
            orderBy("ts", "desc"),
            limit(9500) // bajo el límite duro de 10k
        );
        const eventsMap = new Map();
        const unsub = onSnapshot(q1, (snap) => {
            snap.docChanges().forEach((ch) => {
                if (ch.type === "removed") {
                    eventsMap.delete(ch.doc.id);
                } else {
                    eventsMap.set(ch.doc.id, { id: ch.doc.id, ...ch.doc.data() });
                }
            });
            setEvents(Array.from(eventsMap.values()));
            setLoading(false);
        });
        return () => unsub();
    }, []);

    const kpi = useMemo(() => {
        const uniqPhones = new Set();
        let searches = 0, views = 0, adds = 0, orders = 0;
        for (const e of events) {
            if (e.phoneE164) uniqPhones.add(e.phoneE164);
            if (e.type === "search") searches++;
            if (e.type === "product_view") views++;
            if (e.type === "cart_add") adds++;
            if (e.type === "order_created") orders++;
        }
        return { users: uniqPhones.size, searches, views, adds, orders };
    }, [events]);

    // Series por día
    const byDay = useMemo(() => {
        const map = new Map();
        const dayKey = (ts) => new Date(new Date(ts).toDateString()).getTime();
        for (const e of events) {
            const d = dayKey(e.ts || Date.now());
            const row = map.get(d) || { day: d, searches: 0, views: 0, adds: 0, orders: 0 };
            if (e.type === "search") row.searches++;
            if (e.type === "product_view") row.views++;
            if (e.type === "cart_add") row.adds++;
            if (e.type === "order_created") row.orders++;
            map.set(d, row);
        }
        return Array.from(map.values()).sort((a, b) => a.day - b.day).map(r => ({ ...r, label: new Date(r.day).toLocaleDateString("es-AR") }));
    }, [events]);

    // Top búsquedas / productos / banners
    const topAgg = useMemo(() => {
        const searches = {};
        const productViews = {};
        const cartAdds = {};
        const banners = {};
        const modes = { recomprar: 0, ia: 0, manual: 0 };

        for (const e of events) {
            if (e.type === "search") { const q = (e.payload?.q || "").toLowerCase(); if (q) searches[q] = (searches[q] || 0) + 1; }
            if (e.type === "product_view") { const sku = e.payload?.sku; if (sku) productViews[sku] = (productViews[sku] || 0) + 1; }
            if (e.type === "cart_add") { const sku = e.payload?.sku; if (sku) cartAdds[sku] = (cartAdds[sku] || 0) + 1; }
            if (e.type === "banner_click") { const v = `${e.payload?.group}:${e.payload?.value}`; banners[v] = (banners[v] || 0) + 1; }
            if (e.type === "mode") { const m = e.payload?.mode; if (m && modes[m] !== undefined) modes[m]++; }
        }

        const toArr = (obj, take = 10) => Object.entries(obj).sort((a, b) => b[1] - a[1]).slice(0, take).map(([k, v]) => ({ name: k, value: v }));
        return {
            searches: toArr(searches, 15),
            productViews: toArr(productViews, 15),
            cartAdds: toArr(cartAdds, 15),
            banners: toArr(banners, 10),
            modes: Object.entries(modes).map(([name, value]) => ({ name, value }))
        };
    }, [events]);

    if (loading) return <Loader />;

    const COLORS = ["#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"];

    return (
        <div className="max-w-7xl mx-auto p-4">
            {/* Header */}
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <BarChart3 className="h-6 w-6 text-slate-700" />
                    <h2 className="text-2xl font-semibold">Analytics del Catálogo</h2>
                </div>
                <div className="text-slate-500 text-sm">Últimos 30 días</div>
            </div>

            {/* KPIs */}
            <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
                <KpiCard title="Usuarios únicos" value={kpi.users} icon={<Users className="h-4 w-4" />} />
                <KpiCard title="Búsquedas" value={kpi.searches} />
                <KpiCard title="Vistas de producto" value={kpi.views} />
                <KpiCard title="Agregados al carrito" value={kpi.adds} />
                <KpiCard title="Pedidos creados" value={kpi.orders} />
            </div>

            {/* Series */}
            <div className="mt-6 grid md:grid-cols-2 gap-4">
                <ChartCard title="Actividad diaria (vistas y adds)">
                    <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={byDay}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="views" name="Vistas" stroke="#3b82f6" strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="adds" name="Adds" stroke="#10b981" strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Pedidos por día">
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={byDay}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="orders" name="Pedidos" fill="#ef4444" />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            {/* Topes */}
            <div className="mt-6 grid md:grid-cols-2 gap-4">
                <ChartCard title="Top búsquedas">
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={topAgg.searches}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" name="Veces" fill="#8b5cf6" />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Distribución de modos">
                    <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                            <Pie data={topAgg.modes} dataKey="value" nameKey="name" outerRadius={100}>
                                {topAgg.modes.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-4">
                <ListCard title="Top productos (vistas)" items={topAgg.productViews} />
                <ListCard title="Top productos (adds)" items={topAgg.cartAdds} />
            </div>

            <div className="mt-6">
                <ListCard title="Top banners clickeados" items={topAgg.banners} />
            </div>
        </div>
    );
}

/* ================================
   CustomerProfiles
   Perfiles por cliente desde eventos
================================ */
export function CustomerProfiles() {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const latestEventsRef = useRef([]);

    // 🔴 Recalcula perfiles a partir de una lista de eventos
    const rebuildFromEvents = useCallback((events) => {
        const byPhone = new Map();

        for (const e of events) {
            const p = e.phoneE164 || "anon";
            const row = byPhone.get(p) || {
                phoneE164: p, firstTs: e.ts, lastTs: e.ts,
                searches: 0, views: 0, adds: 0, orders: 0, sessions: new Set(),
                searchTerms: {}, viewedSkus: {}, addedSkus: {}, modes: { ia: 0, recomprar: 0, manual: 0 },
                bannerClicks: {},
            };
            row.firstTs = Math.min(row.firstTs, e.ts || Date.now());
            row.lastTs = Math.max(row.lastTs, e.ts || Date.now());
            row.sessions.add(e.sessionId || "");

            if (e.type === "search") {
                row.searches++;
                const q = (e.payload?.q || "").toLowerCase();
                if (q) row.searchTerms[q] = (row.searchTerms[q] || 0) + 1;
            }
            if (e.type === "product_view") {
                row.views++;
                const sku = e.payload?.sku;
                if (sku) row.viewedSkus[sku] = (row.viewedSkus[sku] || 0) + 1;
            }
            if (e.type === "cart_add") {
                row.adds++;
                const sku = e.payload?.sku;
                if (sku) row.addedSkus[sku] = (row.addedSkus[sku] || 0) + 1;
            }
            if (e.type === "order_created") row.orders++;
            if (e.type === "mode") {
                const m = e.payload?.mode;
                if (m && row.modes[m] !== undefined) row.modes[m]++;
            }
            if (e.type === "banner_click") {
                const v = `${e.payload?.group}:${e.payload?.value}`;
                row.bannerClicks[v] = (row.bannerClicks[v] || 0) + 1;
            }

            byPhone.set(p, row);
        }

        const toTop = (obj, take = 10) =>
            Object.entries(obj)
                .sort((a, b) => b[1] - a[1])
                .slice(0, take)
                .map(([k, v]) => ({ key: k, count: v }));

        const list = Array.from(byPhone.values()).map(r => ({
            phoneE164: r.phoneE164, firstTs: r.firstTs, lastTs: r.lastTs,
            sessions: r.sessions.size, searches: r.searches, views: r.views, adds: r.adds, orders: r.orders,
            topSearches: toTop(r.searchTerms, 8),
            topViewedSkus: toTop(r.viewedSkus, 8),
            topAddedSkus: toTop(r.addedSkus, 8),
            modes: r.modes,
            topBanners: toTop(r.bannerClicks, 6),
            scoreIntent: (r.adds * 2 + r.views + r.searches * 0.5) - r.orders * 3,
        }));

        setProfiles(list.sort((a, b) => b.scoreIntent - a.scoreIntent));
    }, []);

    useEffect(() => {
        setLoading(true);
        const since = Date.now() - 60 * 24 * 60 * 60 * 1000; // 60 días
        const q1 = query(
            collection(db, "catalogEvents"),
            where("ts", ">=", since),
            orderBy("ts", "desc"),
            limit(9500) // seguro bajo 10k
        );
        const eventsMap = new Map();
        const unsub = onSnapshot(q1, (snap) => {
            snap.docChanges().forEach((ch) => {
                if (ch.type === "removed") {
                    eventsMap.delete(ch.doc.id);
                } else {
                    eventsMap.set(ch.doc.id, { id: ch.doc.id, ...ch.doc.data() });
                }
            });
            const arr = Array.from(eventsMap.values());
            latestEventsRef.current = arr;
            rebuildFromEvents(arr);
            setLoading(false);
        });
        return () => unsub();
    }, [rebuildFromEvents]);

    async function saveProfilesToFirestore() {
        setSaving(true);
        try {
            for (const p of profiles) {
                const ref = doc(collection(db, "customerProfiles"));
                await setDoc(ref, { ...p, updatedAt: serverTimestamp() });
            }
            alert("Perfiles guardados en customerProfiles");
        } finally {
            setSaving(false);
        }
    }


    return (
        <div className="max-w-7xl mx-auto p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Users className="h-6 w-6 text-slate-700" />
                    <h2 className="text-2xl font-semibold">Perfiles de Clientes</h2>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => rebuildFromEvents(latestEventsRef.current)} className="h-10 px-3 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 inline-flex items-center gap-2">
                        <RefreshCw className="h-4 w-4" /> Recalcular
                    </button>
                    <button disabled={!profiles.length || saving} onClick={saveProfilesToFirestore} className={cx("h-10 px-3 rounded-xl inline-flex items-center gap-2 text-white", (!profiles.length || saving) ? "bg-slate-300 cursor-not-allowed" : "bg-red-600 hover:bg-red-500")}>
                        Guardar en Firestore
                    </button>
                </div>
            </div>

            {loading ? <Loader /> : (
                <div className="mt-4 grid gap-4">
                    {profiles.slice(0, 200).map(p => (
                        <div key={p.phoneE164} className="rounded-2xl border border-slate-200 bg-white p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-semibold">{p.phoneE164}</p>
                                    <p className="text-xs text-slate-500">Primera visita: {new Date(p.firstTs).toLocaleDateString("es-AR")} • Última: {new Date(p.lastTs).toLocaleDateString("es-AR")}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm">Sesiones: <b>{p.sessions}</b> • Búsquedas: <b>{p.searches}</b> • Vistas: <b>{p.views}</b> • Adds: <b>{p.adds}</b> • Órdenes: <b>{p.orders}</b></p>
                                </div>
                            </div>

                            <div className="mt-3 grid md:grid-cols-4 gap-3">
                                <MiniList title="Top búsquedas" items={p.topSearches} />
                                <MiniList title="Vistos (SKU)" items={p.topViewedSkus} />
                                <MiniList title="Agregados (SKU)" items={p.topAddedSkus} />
                                <MiniList title="Banners" items={p.topBanners} />
                            </div>

                            <div className="mt-3 grid md:grid-cols-3 gap-3">
                                <ChartCard title="Preferencias de modo">
                                    <ResponsiveContainer width="100%" height={180}>
                                        <BarChart data={Object.entries(p.modes).map(([name, value]) => ({ name, value }))}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Bar dataKey="value" fill="#3b82f6" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </ChartCard>

                                <div className="rounded-2xl border border-slate-200 p-3 bg-white">
                                    <p className="text-sm text-slate-500 mb-1">Score de intención</p>
                                    <p className="text-3xl font-semibold">{p.scoreIntent.toFixed(1)}</p>
                                    <p className="text-xs text-slate-500 mt-1">Heurístico para priorizar llamados / WhatsApp</p>
                                </div>

                                <div className="rounded-2xl border border-slate-200 p-3 bg-white">
                                    <p className="text-sm text-slate-500 mb-1">Recomendación</p>
                                    <p className="text-sm">Si agregó muchos productos y no compró, sugiero llamar / enviar WhatsApp con un bundle de los SKUs más agregados y un incentivo.</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ================================
   Helpers UI (Analytics/Profiles)
================================ */
function KpiCard({ title, value, icon }) {
    return (
        <div className="rounded-2xl border border-slate-200 p-4 bg-white">
            <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">{title}</p>
                {icon}
            </div>
            <p className="text-2xl font-semibold mt-1">{value}</p>
        </div>
    );
}
function ChartCard({ title, children }) {
    return (
        <div className="rounded-2xl border border-slate-200 p-4 bg-white">
            <p className="text-sm text-slate-500 mb-2">{title}</p>
            {children}
        </div>
    );
}
function ListCard({ title, items }) {
    return (
        <div className="rounded-2xl border border-slate-200 p-4 bg-white">
            <p className="text-sm text-slate-500 mb-2">{title}</p>
            <div className="max-h-[320px] overflow-auto pr-2">
                <table className="w-full text-sm">
                    <tbody>
                        {items.map((r, i) => (
                            <tr key={i} className="border-t border-slate-100">
                                <td className="p-2 whitespace-nowrap">{r.name}</td>
                                <td className="p-2 text-right">{r.value}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
function MiniList({ title, items }) {
    return (
        <div className="rounded-2xl border border-slate-200 p-3 bg-white">
            <p className="text-sm text-slate-500 mb-2">{title}</p>
            <ul className="text-sm space-y-1">
                {items.map(it => (<li key={it.key} className="flex justify-between"><span className="truncate">{it.key}</span><span className="text-slate-500">{it.count}</span></li>))}
            </ul>
        </div>
    );
}

/* ================================
   Notas de índices Firestore sugeridos
================================ */
// clients: index por phoneE164 (==)
// orders: índice compuesto (cuit asc, createdAt desc)
// catalogEvents: index por ts desc, y opcional phoneE164 asc
